import { StatusPill } from "./StatusPill";
import { JsonViewer } from "./JsonViewer";
import type { StepResult } from "../../types/saga";

export interface StepTimelineProps {
  steps: StepResult[];
}

function formatDuration(start: string, end?: string): string {
  if (!end) return "—";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export function StepTimeline({ steps }: StepTimelineProps) {
  if (steps.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic py-4 text-center">
        Nenhum step executado ainda.
      </div>
    );
  }

  return (
    <ol className="space-y-3" data-testid="step-timeline">
      {steps.map((step) => (
        <li key={step.step} className="rounded-md border border-border bg-surface p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs font-mono text-muted-foreground">#{step.step}</span>
              <span className="font-medium truncate">{step.name}</span>
              <StatusPill status={step.status} />
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground whitespace-nowrap">
              <span>attempts: {step.attempts}</span>
              <span>{formatDuration(step.startedAt, step.finishedAt)}</span>
            </div>
          </div>

          {step.error && (
            <div className="mt-2 text-xs text-danger bg-danger/5 border border-danger/20 rounded px-2 py-1">
              <strong>Erro:</strong> {step.error.message}
            </div>
          )}

          {step.heartbeats && step.heartbeats.length > 0 && (() => {
            const latest = step.heartbeats[step.heartbeats.length - 1]!;
            const isStepActive = step.status === "PENDING";
            return (
              <div className="mt-2">
                {latest.percent !== undefined && (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-border overflow-hidden">
                      <div
                        className={isStepActive ? "h-full bg-primary transition-all duration-500" : "h-full bg-muted-foreground/40"}
                        style={{ width: `${Math.max(0, Math.min(100, latest.percent))}%` }}
                      />
                    </div>
                    <span className="text-xs font-mono-tight text-muted-foreground tabular-nums w-10 text-right">
                      {Math.round(latest.percent)}%
                    </span>
                  </div>
                )}
                {latest.message && (
                  <p className="mt-1 text-xs text-muted-foreground italic truncate">
                    {latest.message}
                  </p>
                )}
                {step.heartbeats.length > 1 && (
                  <details className="mt-1">
                    <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                      heartbeats ({step.heartbeats.length})
                    </summary>
                    <ul className="mt-1 space-y-0.5 text-xs font-mono-tight text-muted-foreground">
                      {step.heartbeats.map((hb, i) => (
                        <li key={i}>
                          <span className="opacity-60">{new Date(hb.ts).toLocaleTimeString("pt-BR")}</span>
                          {hb.percent !== undefined && <span className="ml-2">{hb.percent}%</span>}
                          {hb.message && <span className="ml-2">{hb.message}</span>}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            );
          })()}

          {step.input !== undefined && (
            <details className="mt-2">
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                input
              </summary>
              <JsonViewer value={step.input} className="mt-1" />
            </details>
          )}
          {step.output !== undefined && (
            <details className="mt-2">
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                output
              </summary>
              <JsonViewer value={step.output} className="mt-1" />
            </details>
          )}
        </li>
      ))}
    </ol>
  );
}
